import { useMutation, useQuery } from "@tanstack/react-query";
import { axiosInstance } from "../axios";
import { queryClient } from "../queryClient";

/**
 * Calls behind saving somebody else's colour collection.
 *
 * Follows `api/member-profile/member-profile.js` in separating a REJECTED save (HTTP 200 carrying
 * `isSuccess: false`, which the form renders against its fields) from a REFUSED request (a 4xx,
 * which is a message and nothing else). The rate limiter makes that distinction load-bearing here:
 * a 429 is the one outcome where the member should be told to wait and try again, and it arrives as
 * a status code rather than in the body.
 */

/** Fetches the descriptor a save modal renders itself from. */
export const fetchCollectionSaveForm = async (url) => {
  const response = await axiosInstance.get(url);
  return response.data;
};

export const useCollectionSaveForm = (url) =>
  useQuery({
    queryKey: ["collection-save-form", url],
    queryFn: () => fetchCollectionSaveForm(url),
    enabled: !!url,
  });

/**
 * Normalises anything a save can throw into the one shape the callers branch on.
 *
 * @returns {{ isRateLimited: boolean, errors: object|null, message: string|null }}
 */
export const asRefusal = (error) => {
  const status = error?.response?.status;
  const data = error?.response?.data;

  return {
    isRateLimited: status === 429 || data?.isRateLimited === true,
    errors: data?.errors ?? null,
    message: data?.message ?? null,
  };
};

const postSave = async ({ url, body }) => {
  const response = await axiosInstance.post(url, body);
  return response.data;
};

/**
 * Posts a save. Callers pass `successHandler` / `errorHandler` in the variables object, matching
 * every other mutation here.
 *
 * A successful save changes the member's palette and the source collection's count, so the saved
 * colour queries are invalidated before the caller's handler runs — otherwise the header count and
 * the saved-colours modal keep showing the figures from before the save.
 */
export const useSaveCollection = () =>
  useMutation({
    mutationFn: async ({ url, body }) => await postSave({ url, body }),
    onSuccess: (responseData, { successHandler }) => {
      queryClient
        .invalidateQueries({ queryKey: ["my-saved-colors-dialog"] })
        .then(() => {
          if (successHandler) {
            successHandler(responseData);
          }
        });
    },
    onError: (error, { errorHandler }) => {
      if (errorHandler) {
        errorHandler(asRefusal(error));
      }
    },
  });
